function own(source, key) {
  return Boolean(source && Object.prototype.hasOwnProperty.call(source, key))
}

function pick(source, keys, fallback) {
  const key = keys.find(candidate => own(source, candidate))
  return key ? source[key] : fallback
}

function phaseSevenError(response, fallbackMessage = 'API 請求失敗') {
  const payload = response && response.body !== undefined ? response.body : response
  const error = new Error(
    pick(payload, ['message', 'Message'], '') ||
      pick(response, ['statusText', 'StatusText'], '') ||
      fallbackMessage
  )
  error.code = pick(payload, ['code', 'Code'], '')
  error.httpStatus = Number(
    pick(response, ['httpStatus', 'status', 'Status'], pick(payload, ['status', 'Status'], 0))
  )
  return error
}

// Phase 7 was added while two response conventions were still in use. Keep
// transport/envelope compatibility at this boundary so components only see DTOs.
export function normalizePhaseSevenResponse(response) {
  let payload = response
  if (response && response.__httpResponse === true) {
    const status = Number(response.httpStatus)
    if (!Number.isInteger(status) || status < 200 || status >= 300) {
      throw phaseSevenError(response)
    }
    if (status === 204) return null
    payload = response.body
  }

  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    if (own(payload, 'success') || own(payload, 'Success')) {
      const successful = pick(payload, ['success', 'Success'], false)
      if (successful !== true) throw phaseSevenError(payload)
      return pick(payload, ['data', 'Data'], null)
    }
  }

  if (payload === undefined) throw phaseSevenError(payload, 'API 回應格式不正確')
  return payload
}

function normalizeEnabled(source, fallback = false) {
  return pick(source, ['isEnabled', 'IsEnabled', 'enabled', 'Enabled'], fallback) === true
}

export function normalizeTourLeaderSetting(source) {
  const setting = source && typeof source === 'object' ? source : {}
  return {
    ...setting,
    agencyCode: String(pick(setting, ['agencyCode', 'AgencyCode'], '') || ''),
    agencyName: String(pick(setting, ['agencyName', 'AgencyName'], '') || ''),
    isEnabled: normalizeEnabled(setting),
    updatedAt: pick(setting, ['updatedAt', 'UpdatedAt'], ''),
    updatedBy: pick(setting, ['updatedBy', 'UpdatedBy'], ''),
  }
}

export function toTourLeaderSettingsPage(response) {
  const payload = normalizePhaseSevenResponse(response)
  if (Array.isArray(payload)) {
    return { items: payload.map(normalizeTourLeaderSetting), total: payload.length }
  }
  const page = payload && typeof payload === 'object' ? payload : {}
  const items = pick(page, ['items', 'Items', 'data', 'Data'], [])
  const normalizedItems = Array.isArray(items) ? items.map(normalizeTourLeaderSetting) : []
  const total = Number(pick(page, ['total', 'Total'], normalizedItems.length))
  return {
    items: normalizedItems,
    total: Number.isFinite(total) ? total : normalizedItems.length,
  }
}

export function normalizeTourLeaderRecommendation(source) {
  const recommendation = source && typeof source === 'object' ? source : {}
  const parsedSort = Number(pick(recommendation, ['recommendSort', 'RecommendSort'], 99))
  return {
    userId: pick(recommendation, ['userId', 'UserId', 'userID', 'UserID'], null),
    isRecommended: pick(recommendation, ['isRecommended', 'IsRecommended'], false) === true,
    recommendSort: Number.isInteger(parsedSort) ? Math.min(9999, Math.max(0, parsedSort)) : 99,
  }
}

export function normalizeTourLeaderBanners(response) {
  const payload = normalizePhaseSevenResponse(response)
  const container = payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : {}
  const items = Array.isArray(payload)
    ? payload
    : pick(container, ['items', 'Items', 'banners', 'Banners', 'data', 'Data'], [])
  const slotCode = String(
    pick(container, ['slotCode', 'SlotCode', 'bannerSlot', 'BannerSlot'], '') || ''
  )
  return {
    slotCode,
    items: (Array.isArray(items) ? items : []).map(item => ({
      ...item,
      id: pick(item, ['id', 'ID', 'bannerId', 'BannerId'], ''),
      title: String(pick(item, ['title', 'Title', 'name', 'Name'], '') || ''),
      // The admin endpoint returns only currently active banner items. Older
      // payloads may additionally expose an item-level flag.
      isEnabled: normalizeEnabled(item, true),
      sort: Number(pick(item, ['sort', 'Sort', 'displayOrder', 'DisplayOrder'], 99)),
      startAt: pick(item, ['startAt', 'StartAt', 'publishStartAt', 'PublishStartAt'], ''),
      endAt: pick(item, ['endAt', 'EndAt', 'publishEndAt', 'PublishEndAt'], ''),
    })),
  }
}

export function isTourLeaderFeatureDisabled(error) {
  const response = error && error.response
  const payload =
    (error && (error.body || error.data)) ||
    (response && (response.body || response.data)) ||
    error ||
    {}
  const code = String(
    (error && error.code) || pick(payload, ['code', 'Code'], '') || ''
  ).toLowerCase()
  return code === 'feature_disabled' || code === 'tour_leader_agency_disabled'
}
